/* type-coverage:disable-file */
import {isReadableError} from '@/lib/api/ReadableError'
import {responseData} from '@/lib/api/ResponseData'
import throwIfError from '@/lib/api/throwIfError'
import {getCurrency} from '@/lib/locale/country'
import {
  getLanguageCountryFromLocale,
  localeUnderscoreToHyphen,
  type SupportedLocales,
} from '@/lib/locale/locale'
import {CartCalibratedError} from '@/services/cartService'
import call from '@/utils/api/call'
import shouldLogError from '@/utils/api/shouldLogError'
import UnexpectedError from '@/utils/api/UnexpectedError'
import UserFriendlyError from '@/utils/errors/UserFriendlyError'
import waitAtLeast from '@/utils/waitAtLeast'

import foodCartDb from './foodCartDb'
import type {FoodCartItemWithoutQuantity} from './foodCartOperations'
import * as foodCartOperations from './foodCartOperations'
import {FoodCartV1, FoodPaymentResponse, OrderResponse, Orders,OrderTrackingResponse} from './foodModel'

const NON_ERROR_API_CODES = ['ICA0000']

const FoodCartV1Response = responseData(FoodCartV1)

export async function getFoodCart({
  locale,
  signal,
  isAuthenticated,
}: {
  isAuthenticated: boolean
  locale: SupportedLocales
  signal?: AbortSignal
}) {
  const {country} = getLanguageCountryFromLocale(locale)

  if (!isAuthenticated) return waitAtLeast(Promise.resolve(foodCartDb.getFoodCart(country)), 300)

  return call
    .get('/api/checkout/v1/food-carts', {
      signal,
      headers: {
        'Accept-Language': localeUnderscoreToHyphen(locale),
      },
    })
    .then(res => {
      throwIfError(res, NON_ERROR_API_CODES)
      const data = FoodCartV1Response(res.data)
      return data.data
    })
    .catch(e => {
      if (shouldLogError(e)) {
        console.error('Cannot fetch food cart', e)
      }
      throw new UnexpectedError(e)
    })
}

export async function addToFoodCart({
  isAuthenticated,
  item,
  quantity,
  locale,
  signal,
}: {
  isAuthenticated: boolean
  item: FoodCartItemWithoutQuantity
  quantity?: number
  locale: SupportedLocales
  signal?: AbortSignal
}) {
  const {country} = getLanguageCountryFromLocale(locale)

  let currentFoodCart: FoodCartV1

  if (!isAuthenticated) {
    currentFoodCart = foodCartDb.getFoodCart(country)
  } else {
    currentFoodCart = await getFoodCart({locale, isAuthenticated})
  }

  const newFoodCart = foodCartOperations.addToFoodCart({
    foodCart: currentFoodCart,
    item,
    quantity,
  })

  if (!isAuthenticated) {
    return foodCartDb.updateFoodCart({country, foodCart: newFoodCart})
  }

  return call
    .patch('/api/checkout/v1/food-carts', newFoodCart, {
      signal,
      headers: {
        'Accept-Language': localeUnderscoreToHyphen(locale),
      },
    })
    .then(res => {
      throwIfError(res, NON_ERROR_API_CODES)
      const data = FoodCartV1Response(res.data)
      return data.data
    })
    .catch(e => {
      if (shouldLogError(e)) {
        console.error('Cannot add to food cart', e)
      }
      throw new UnexpectedError(e)
    })
}

export async function removeFromFoodCart({
  isAuthenticated,
  itemOrIndex,
  quantity,
  locale,
  signal,
}: {
  isAuthenticated: boolean
  itemOrIndex: FoodCartItemWithoutQuantity | number
  quantity?: number // if quantity is 0, <0 or missing, remove the item entirely
  locale: SupportedLocales
  signal?: AbortSignal
}) {
  const {country} = getLanguageCountryFromLocale(locale)

  let currentFoodCart: FoodCartV1

  if (!isAuthenticated) {
    currentFoodCart = foodCartDb.getFoodCart(country)
  } else {
    currentFoodCart = await getFoodCart({locale, isAuthenticated})
  }

  const newFoodCart = foodCartOperations.removeFromFoodCart({
    foodCart: currentFoodCart,
    itemOrIndex,
    quantity,
  })

  if (!isAuthenticated) {
    return waitAtLeast(
      Promise.resolve(foodCartDb.updateFoodCart({country, foodCart: newFoodCart})),
      300,
    )
  }

  return call
    .patch('/api/checkout/v1/food-carts', newFoodCart, {
      signal,
      headers: {
        'Accept-Language': localeUnderscoreToHyphen(locale),
      },
    })
    .then(res => {
      throwIfError(res, NON_ERROR_API_CODES)
      const data = FoodCartV1Response(res.data)
      return data.data
    })
    .catch(e => {
      if (shouldLogError(e)) {
        console.error('Cannot remove from food cart', e)
      }
      throw new UnexpectedError(e)
    })
}

export async function updateFoodCartProductQuantity({
  isAuthenticated,
  itemOrIndex,
  quantity,
  locale,
  signal,
}: {
  isAuthenticated: boolean
  itemOrIndex: FoodCartItemWithoutQuantity | number
  quantity: number
  locale: SupportedLocales
  signal?: AbortSignal
}) {
  const {country} = getLanguageCountryFromLocale(locale)

  let currentFoodCart: FoodCartV1

  if (!isAuthenticated) {
    currentFoodCart = foodCartDb.getFoodCart(country)
  } else {
    currentFoodCart = await getFoodCart({locale, isAuthenticated})
  }

  const newFoodCart = foodCartOperations.updateFoodCartProductQuantity({
    foodCart: currentFoodCart,
    itemOrIndex,
    quantity,
  })

  if (!isAuthenticated) {
    return waitAtLeast(
      Promise.resolve(foodCartDb.updateFoodCart({country, foodCart: newFoodCart})),
      300,
    )
  }

  return call
    .patch('/api/checkout/v1/food-carts', newFoodCart, {
      signal,
      headers: {
        'Accept-Language': localeUnderscoreToHyphen(locale),
      },
    })
    .then(res => {
      throwIfError(res, NON_ERROR_API_CODES)
      const data = FoodCartV1Response(res.data)
      return data.data
    })
    .catch(e => {
      if (shouldLogError(e)) {
        console.error('Cannot update food cart product quantity', e)
      }
      throw new UnexpectedError(e)
    })
}

// There are 3 cases:
// 1. Buy now for users: items are provided
// 2. Buy cart for users: items are not provided
// 3. For guests: if items are provided, use them, otherwise use the local cart
export async function createFoodPaymentIntent({
  isAuthenticated,
  confirmationTokenId,
  returnUrl,
  signal,
  applyRewards,
  coupon,
  locale,
  email,
  name,
  phone,
  line1,
  line2,
  city,
  state,
  postalCode,
  roundUp,
  longitude,
  latitude,
  notes,
  scheduledId,
}: {
  locale: SupportedLocales
  signal?: AbortSignal
  confirmationTokenId: string
  isAuthenticated: boolean
  applyRewards: boolean
  roundUp: boolean
  returnUrl: string
  queryClient: QueryClient
  coupon?: string
  email: string
  name: string
  phone: string
  line1: string
  line2: string
  city: string
  state: string
  postalCode: string
  longitude: number
  latitude: number
  notes: string
  scheduledId: string | undefined
}) {
  const willUseGuestCart = !isAuthenticated

  const {country} = getLanguageCountryFromLocale(locale)
  const currency = getCurrency(country)?.toLowerCase()

  if (!currency) {
    throw new UserFriendlyError('Unsupported country', new Error()).permanent()
  }

  const api = (() => {
    if (isAuthenticated) {
      // Buy cart for users
      return '/api/checkout/v1/payments/payment-intents-for-food-cart'
    }

    // For guests
    return '/api/checkout/pub/v1/payments/payment-intents-for-food'
  })()

  const shippingInfo = {
    email,
    name,
    phone,
    line1,
    line2,
    city,
    state,
    postalCode,
    country,
  }

  const commonData = {
    confirmationTokenId,
    shippingInfo,
    checkoutType: 'NO_OP',
    userLongitude: longitude,
    userLatitude: latitude,
    scheduledId,
    userDropoffNotes: notes || 'No notes',
    code: coupon,
    roundUp,
    returnUrl: location.origin + returnUrl,
  }

  const data = willUseGuestCart
    ? await (async () => {
        const {v4: uuidV4} = await import('uuid')

        const uuid = uuidV4()
        return {
          ...commonData,
          cartId: uuid,
          email,
          items: foodCartDb.getFoodCart(country).foodCartItems,
        }
      })()
      : {
        ...commonData,
        useWallet: applyRewards,
      }

  return call
    .post(api, data, {
      signal,
      headers: {
        'Accept-Language': localeUnderscoreToHyphen(locale),
      },
    })
    .then(res => {
      const data = res.data as object // type-coverage:ignore-line
      if ('code' in data && typeof data.code === 'string' && data.code === 'ICA1120') {
        throw new CartCalibratedError(data)
      }

      throwIfError(res, NON_ERROR_API_CODES)

      const foodPaymentData = FoodPaymentResponse(res.data).data

      const order = foodPaymentData.order

      return {
        clientSecret: foodPaymentData.clientSecret,
        order,
        paymentIntentStatus: foodPaymentData.paymentIntentStatus,
      }
    })
    .catch(e => {
      if (shouldLogError(e)) {
        console.error('Cannot checkout cart', e)
      }

      if (isReadableError(e)) {
        throw new UserFriendlyError(e.res.data.msg as string, e) // type-coverage:ignore-line
      }
      throw new UnexpectedError(e)
    })
}

export async function getFoodOrderTracking({
  foodOrderId,
  signal,
}: {
  foodOrderId: string
  signal?: AbortSignal
}) {
  return call
    .get(`/api/checkout/pub/v1/food-orders/${foodOrderId}/tracking`, {
      signal,
    })
    .then(res => {
      throwIfError(res, NON_ERROR_API_CODES)
      const data = OrderTrackingResponse(res.data)
      return data.data
    })
}

export async function getFoodOrder({
  foodOrderId,
  signal,
}: {
  foodOrderId: string
  signal?: AbortSignal
}) {
  return call
    .get(`/api/checkout/v1/food-orders/${foodOrderId}`, {
      signal,
    })
    .then(res => {
      throwIfError(res, NON_ERROR_API_CODES)
      const data = OrderResponse(res.data)
      return data.data
    })
}

export async function getFoodOrders({
  signal,
  locale,
}: {
  signal?: AbortSignal
  locale: SupportedLocales
}) {
  return call
    .get('/api/checkout/v1/food-orders', {
      signal,
      headers: {
        'Accept-Language': localeUnderscoreToHyphen(locale),
      },
    })
    .then(res => {
      throwIfError(res, NON_ERROR_API_CODES)
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- Safe: API returns { data: { records: Order[] } }
      const records = res.data.data?.records

      if (Array.isArray(records)) return Orders(records)

      return []
    })
}
